import React, { useEffect, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";

import { doc, onSnapshot, collection, addDoc, serverTimestamp } from "firebase/firestore";
import app, { auth, db } from "../../firebase/client";
import { subscribeBoardMessages } from "../../utils/boardFirestore";
import { deviceTimeZone, isMessageVisible, quietHoursActive, alarmState, formatAlarm } from "../../utils/time";
import { playChime, playAlarm } from "../../utils/chime";
import useResidentPhotos from "../../hooks/useResidentPhotos";
import PhotoSlideshow from "../../components/PhotoSlideshow/PhotoSlideshow";
import useElementSize from "../../hooks/useElementSize";
import { isBillingBlocked } from "../../components/Billing/BillingBanner";
import BatteryIndicator from "../../components/BatteryIndicator/BatteryIndicator";
import useBatteryStatus from "../../hooks/useBatteryStatus";
import BigClock from "../../components/BigClock/BigClock";

// Per-resident message font size on the display (keys mirror CaregiverBoard).
const DISPLAY_FONT = { small: "3.5vh", medium: "5vh", large: "6.5vh" };

// localStorage keys for this tablet's stable identity + its one-time secret.
const DEVICE_ID_KEY = "mab_device_id";
const DEVICE_SECRET_KEY = "mab_device_secret";
// How often (ms) an unclaimed kiosk re-checks whether it has been claimed.
const POLL_INTERVAL = 5000;

const functions = getFunctions(app, "us-central1");
const registerDeviceFn = httpsCallable(functions, "registerDevice");
const mintDeviceTokenFn = httpsCallable(functions, "mintDeviceToken");
const reportDeviceStatusFn = httpsCallable(functions, "reportDeviceStatus");

const getOrCreateDeviceId = () => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = uuidv4();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

const Display = () => {
  const [pairingCode, setPairingCode] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [boardId, setBoardId] = useState(null);
  const [items, setItems] = useState([]);
  const battery = useBatteryStatus(); // reported up so admins can monitor screens
  const [resident, setResident] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const [pairingNonce, setPairingNonce] = useState(0); // bump to restart the register+poll cycle
  const [boardRef, size] = useElementSize();

  // Return the kiosk to the setup-code screen and restart pairing — used when
  // the screen is unlinked remotely (e.g. its resident is deleted). No page
  // reload, so it works in locked-down kiosk mode.
  const resetToPairing = () => {
    setClaimed(false);
    setBoardId(null);
    setResident(null);
    setTenant(null);
    setItems([]);
    setPairingCode(null);
    setPairingNonce((n) => n + 1);
  };

  // Live clock so reminders flip to warning (glow) / past (grey) on their own.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // Register on mount, then poll mintDeviceToken until claimed. Once claimed,
  // sign in with the returned custom token (device-scoped claims) so this kiosk
  // can read ONLY its board. No anonymous auth.
  useEffect(() => {
    const deviceId = getOrCreateDeviceId();
    let cancelled = false;

    const tryMint = async () => {
      const secret = localStorage.getItem(DEVICE_SECRET_KEY);
      if (!secret) return; // registration hasn't produced a secret yet
      try {
        const { data } = await mintDeviceTokenFn({ deviceId, deviceSecret: secret });
        if (cancelled || !data.claimed) return;
        await signInWithCustomToken(auth, data.token);
        if (cancelled) return;
        setBoardId(data.boardId);
        setClaimed(true);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch (err) {
        /* transient; keep polling */
      }
    };

    const register = async () => {
      try {
        const { data } = await registerDeviceFn({ deviceId });
        if (cancelled) return;
        if (data.deviceSecret) localStorage.setItem(DEVICE_SECRET_KEY, data.deviceSecret);
        setPairingCode(data.pairingCode);
        await tryMint(); // in case it was already claimed
      } catch (err) {
        if (!cancelled) setError("Could not reach the server. Retrying…");
      }
    };

    register();
    pollRef.current = setInterval(tryMint, POLL_INTERVAL);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairingNonce]);

  // Once claimed, watch our OWN device doc (readable via the stable deviceId
  // claim, even after the board is gone). This self-heals the kiosk without a
  // reload: if the screen is unlinked (resident deleted) it returns to the setup
  // code; if it's reassigned to a different resident, it re-mints and switches
  // boards live.
  useEffect(() => {
    if (!claimed) return;
    const deviceId = getOrCreateDeviceId();
    return onSnapshot(
      doc(db, "devices", deviceId),
      { includeMetadataChanges: true },
      async (s) => {
        // Act only on SERVER-confirmed state. Right after a (re)claim, Firestore
        // first replays a stale cached copy of this doc (e.g. residentId:null from
        // when it was just unlinked); acting on that would bounce the kiosk back to
        // the setup code and fight the fresh claim — a rapid flicker. Ignoring
        // cached snapshots (includeMetadataChanges lets the server copy always
        // arrive, even when the data is unchanged) fixes it.
        if (s.metadata.fromCache) return;
        const d = s.exists() ? s.data() : null;
        if (!d || !d.residentId || !d.tenantId) { resetToPairing(); return; } // unlinked
        if (d.residentId === boardId) return; // no change
        try { // reassigned -> mint a token for the new board and switch
          const secret = localStorage.getItem(DEVICE_SECRET_KEY);
          const { data } = await mintDeviceTokenFn({ deviceId, deviceSecret: secret });
          if (data.claimed) {
            await signInWithCustomToken(auth, data.token);
            setItems([]);
            setResident(null);
            setBoardId(data.boardId);
          }
        } catch (_) { /* transient; a later snapshot will retry */ }
      },
      (err) => console.error("device watch failed:", err)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimed, boardId]);

  // Once claimed + boardId known, subscribe to this board's messages.
  useEffect(() => {
    if (!claimed || !boardId) return;
    const unsubscribe = subscribeBoardMessages(db, boardId, setItems, (err) =>
      console.error("display board subscribe failed:", err)
    );
    return unsubscribe;
  }, [claimed, boardId]);

  // Live resident doc (name for the banner + message font size).
  useEffect(() => {
    if (!claimed || !boardId) return;
    return onSnapshot(doc(db, "residents", boardId),
      (s) => setResident(s.exists() ? s.data() : null),
      (err) => console.error("display resident subscribe failed:", err));
  }, [claimed, boardId]);

  // Report battery/charge up to this device's doc so admins can monitor screens.
  // Fires on any level/charging change and on a 15-min heartbeat (also keeps
  // statusAt fresh). No-ops on devices with no battery source (e.g. laptops).
  useEffect(() => {
    if (!claimed || !battery) return;
    const report = () => reportDeviceStatusFn({
      battery: Math.round(battery.level * 100),
      charging: !!battery.charging,
    }).catch(() => {});
    report();
    const id = setInterval(report, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [claimed, battery && battery.level, battery && battery.charging]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live tenant doc: if the care circle's billing lapses (canceled/unpaid), the
  // display is paused (content hidden, not deleted) until billing is restored.
  useEffect(() => {
    const tid = resident?.tenantId;
    if (!tid) { setTenant(null); return; }
    return onSnapshot(doc(db, "tenants", tid),
      (s) => setTenant(s.exists() ? { id: s.id, ...s.data() } : null),
      (err) => console.error("display tenant subscribe failed:", err));
  }, [resident?.tenantId]);

  const paused = isBillingBlocked(tenant);
  const noteFont = DISPLAY_FONT[resident?.fontSize] || DISPLAY_FONT.small;
  const tz = resident?.timezone || deviceTimeZone();
  // Daily quiet hours: go dark between quietFrom/quietTo (HH:MM, resident tz).
  const hhmmToMin = (s) => { if (!s) return null; const [h, m] = String(s).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const screenOff = quietHoursActive(hhmmToMin(resident?.quietFrom), hhmmToMin(resident?.quietTo), now, tz);
  // Optional resident photo slideshow: right 40% panel; notes reflow into left 60%.
  const photos = useResidentPhotos(boardId, resident?.tenantId);
  const photoActive = photos.length > 0;
  // Messages/alarms currently on screen.
  const visibleItems = items.filter((it) => isMessageVisible(it, now, tz));
  const hasMsgs = visibleItems.length > 0;
  // Where the big clock goes, by (messages × slideshow) presence:
  //   none + none   -> full screen
  //   none + photos -> left of the slideshow
  //   msgs + none   -> in the slideshow's spot (right)
  //   msgs + photos -> no clock (the normal notes + photos layout)
  const clockMode = !hasMsgs && !photoActive ? "full"
    : !hasMsgs && photoActive ? "left"
    : hasMsgs && !photoActive ? "right"
    : null;
  // Notes sit in the left 60% whenever the right pane holds photos or the clock.
  const scaleX = (photoActive || clockMode === "right") ? 0.6 : 1;

  // New-message chime (per-resident, admin-controlled): play a soft chime when a
  // message NEWLY appears on the screen. seenChimeRef tracks the message ids that
  // are currently visible; it starts null so the first data load (and every board
  // switch) baselines silently — only messages that appear afterward chime. A
  // scheduled message chimes when its show-from arrives (it becomes visible then).
  const chimeOn = !!resident?.chimeOnNew;
  const seenChimeRef = useRef(null);
  useEffect(() => { seenChimeRef.current = null; }, [boardId]); // re-baseline per board
  useEffect(() => {
    if (!claimed) return;
    const visibleIds = items.filter((it) => isMessageVisible(it, now, tz)).map((it) => it.id);
    if (seenChimeRef.current === null) { // first pass: remember what's already showing
      seenChimeRef.current = new Set(visibleIds);
      return;
    }
    const isNew = visibleIds.some((id) => !seenChimeRef.current.has(id));
    // Track exactly what's visible now (prunes removed msgs so a repost re-chimes).
    seenChimeRef.current = new Set(visibleIds);
    if (isNew && chimeOn && !screenOff && !paused) playChime();
  }, [items, now, tz, chimeOn, screenOff, paused, claimed]);

  // Alarm chime: play a distinct chime when a visible alarm ENTERS its warning
  // (glow) state — the moment it "goes off". Tracks each alarm's last state so it
  // fires once per transition (not every clock tick), baselines silently on first
  // load, and stays quiet during quiet hours / billing pause.
  const alarmStatesRef = useRef(null);
  useEffect(() => { alarmStatesRef.current = null; }, [boardId]); // re-baseline per board
  useEffect(() => {
    if (!claimed) return;
    const prev = alarmStatesRef.current;
    const cur = new Map();
    let wentOff = false;   // an alarm entered its glow/warning state -> chime
    let ended = false;     // an alarm's glow stopped (went to past/grey)
    for (const it of items) {
      if (!isMessageVisible(it, now, tz)) continue;
      const st = alarmState(it, now, tz);
      if (st === "none") continue;
      cur.set(it.id, st);
      const was = prev ? prev.get(it.id) : undefined;
      if (prev && st === "warning" && was !== "warning") wentOff = true;
      if (prev && st === "past" && was === "warning") ended = true;
    }
    alarmStatesRef.current = cur;
    // Append an event-log entry (metadata only) tagged with this screen.
    const tid = resident?.tenantId;
    const emit = (action) => {
      if (!boardId || !tid) return;
      addDoc(collection(db, "boards", boardId, "events"), {
        ts: serverTimestamp(), category: "device", type: "alarm", action,
        tenantId: tid, deviceId: getOrCreateDeviceId(), pairingCode: pairingCode || null,
      }).catch(() => {});
    };
    if (prev && wentOff && !screenOff && !paused) { playAlarm(); emit("chimed"); }
    if (prev && ended) emit("ended");
  }, [items, now, tz, screenOff, paused, claimed, boardId, resident, pairingCode]);

  // --- Unclaimed: show the pairing code -----------------------------------
  if (!claimed) {
    return (
      <div className="display-pairing">
        <h1>Mary's Bulletin Board</h1>
        <p className="display-pairing-label">Setup code</p>
        <div className="display-pairing-code">{pairingCode || "……"}</div>
        <p className="display-pairing-help">
          Provide this code to your Care Circle members to connect this board.
        </p>
        <p className="display-pairing-note">This board looks best in landscape orientation.</p>
        {error && <p className="display-error">{error}</p>}
      </div>
    );
  }

  // --- Quiet hours: dark screen (content preserved, resumes after the window) ---
  if (screenOff) {
    return <div style={{ position: "fixed", inset: 0, background: "#000" }} />;
  }

  // --- Claimed but billing lapsed: pause the screen (content preserved) ----
  if (paused) {
    return (
      <div className="display-pairing">
        <h1>Mary's Bulletin Board</h1>
        <p className="display-pairing-help">
          This board is paused. Please ask the Care Circle admin to update billing to
          restore it.
        </p>
      </div>
    );
  }

  // --- Claimed: date/day/time banner + read-only board --------------------
  // Notes store fractional positions (0..1); multiply by this board's measured
  // pixel size so the layout matches regardless of tablet resolution.
  return (
    <div className="display-screen">
      {resident?.name && (
        <div className="display-name-banner">
          <span className="display-board-title">{resident.name}'s message board</span>
          {(tenant?.name || tenant?.adminName) && (
            <span className="display-family-circle">
              {tenant?.name && <strong>{tenant.name}</strong>}
              {tenant?.adminName && <>{tenant?.name ? "  ·  " : ""}Admin: <strong>{tenant.adminName}</strong></>}
            </span>
          )}
          {pairingCode && (
            <span className="display-screen-code" title="This screen's setup code — used to identify or reconnect it">
              Screen code: {pairingCode}
            </span>
          )}
          <BatteryIndicator />
        </div>
      )}
      {/* The small date/day/time strip is redundant while the big clock shows, so
          hide it then; the name/care-circle/admin/screen-code/battery banner stays. */}
      {!clockMode && (
        <div className="display-banner">
          {/* One timezone-aware clock everywhere (resident tz), styled as a
              horizontal strip here — no more device-local Time/Day/DateDiv. */}
          <div className="dc-banner"><BigClock tz={tz} /></div>
        </div>
      )}
      <div className="display-board" ref={boardRef}>
        {photoActive && (
          <div className="display-photo-panel">
            <PhotoSlideshow photos={photos} imgClassName="display-photo-img" />
          </div>
        )}
        {clockMode && (
          <div className={"display-clock display-clock-" + clockMode}>
            <BigClock tz={tz} />
          </div>
        )}
        {size.width > 0 &&
          visibleItems.map((it) => {
            const st = alarmState(it, now, tz); // upcoming (plain) -> warning (glow) -> past (grey)
            const cls = "display-note"
              + (st === "warning" ? " reminder-warn" : st === "past" ? " reminder-past" : "");
            return (
              <div
                key={it.id}
                className={cls}
                style={{
                  backgroundColor: it.color,
                  fontSize: noteFont,
                  left: (it.pos?.x ?? 0.4) * scaleX * size.width,
                  top: (it.pos?.y ?? 0.15) * size.height
                }}
              >
                {it.alert && it.actionAt != null && (
                  <div className="reminder-time">⏰ {formatAlarm(it, tz)}</div>
                )}
                {it.item}
                {it.authorName && <div className="note-from-display">— {it.authorName}</div>}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Display;
